/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';

import { parseHTML, isHTML } from 'helpers/parser';
import { Container, Label, Paragraph, Collapse, CrossIcon } from 'ui/components/styled';

const defaults = {
  visibleFields: ['from', 'subject', 'received', 'headers'],
  maxBodyHeight: '500px',
};

export const EmailPreview = ({
  email,
  visibleFields = defaults.visibleFields,
  maxBodyHeight = defaults.maxBodyHeight,
  closable,
  onBodyWidthInit,
  onClose,
  ...rest
}) => {
  const bodyRef = React.useRef();
  const bodyIsHTML = React.useMemo(() => isHTML(email.body), [email?.body]);

  React.useEffect(() => {
    if (bodyRef.current) {
      const rect = bodyRef.current.getBoundingClientRect();
      if (onBodyWidthInit) {
        onBodyWidthInit(rect.width);
      }
    }
  }, [bodyRef]);

  return (
    <Container {...rest}>
      {visibleFields?.length > 0 && (
        <Container
          flexDirection="row"
          bg="#f5f9fe"
          height="auto"
          borderRadius="8px"
          px="s"
          py="s"
          pb="xs"
          mb="s"
        >
          <Container width="90%" flexGrow="1">
            {visibleFields?.includes('from') && (
              <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
                <Label mr="xxs">From:</Label>
                <Paragraph>{email.from}</Paragraph>
              </Container>
            )}
            {visibleFields?.includes('subject') && (
              <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
                <Label mr="xxs">Subject:</Label>
                <Paragraph>{email.subject}</Paragraph>
              </Container>
            )}
            {visibleFields?.includes('received') && (
              <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
                <Label mr="xxs">Received:</Label>
                <Paragraph>{format(new Date(email.date), 'MMMM do yyyy, h:mm:ss aaa')}</Paragraph>
              </Container>
            )}
            {visibleFields?.includes('headers') && email.headerLines && (
              <Collapse label="Headers" contentProps={{ color: '#555' }}>
                {email.headerLines}
              </Collapse>
            )}
          </Container>
          <Container width="auto">
            {closable && <CrossIcon color="primary" opacity="0.8" onClick={onClose} />}
          </Container>
        </Container>
      )}
      <BodyContainer maxHeight={maxBodyHeight} overflow="auto">
        <Body ref={bodyRef} isHTML={bodyIsHTML}>
          {parseHTML(email.body)}
        </Body>
      </BodyContainer>
    </Container>
  );
};

const Body = styled.div`
  font-weight: normal;
  font-size: 16px;
  letter-spacing: 0.04em;
  line-height: 30px;
  text-align: left;
  color: #333;
  ${({ isHTML }) =>
    isHTML
      ? `
    white-space: normal;
  `
      : `
    word-wrap: break-word;
    white-space: pre-wrap;
  `}
`;

// eslint-disable-next-line no-unused-vars
const BodyContainer = styled(({ maxHeight, ...rest }) => <Container {...rest} />).attrs(
  ({ maxHeight }) => ({
    style: { maxHeight }, // fixes frequently changed styles warning
  })
)``;
